<template>
  <loading-view :loading="!application">
    <div class="pt-8 px-4 tile">
      <form class="pb-4" @submit.prevent="handleSaveApplication">
        <div v-if="isRemoved" class="alert alert-warning">
          <div class="text-center">
            <i class="fa fa-exclamation-triangle status-paused" style="font-size: 40px"></i>
          </div>
          <div>
            <p class="mb-2">Dear Customer,</p>
            <p class="mb-2">
              As we informed you earlier through email, this application is no longer supported by our APIcenter. Your
              integrations using this application are no longer active but still accessable to you for the purpose of
              copying settings.
            </p>

            <p class="mb-2">
              If this application and its settings are no longer required you can savely remove the application from
              your account. All integrations and flows that are depending on this application will also be removed and
              no longer be shown on your dashboard. Please be aware that this action cannot be undone in any way and all
              information stored for the application, integration and flows will be lost.
            </p>
            <p class="mb-2">
              Billing of the integration is also halted and from the 1st of next month you will no longer be charged
              with the fees for running the integrations and flows using this application.
            </p>
            <p class="mb-2">If any question remain you can create a ticket here: &lt;&gt;</p>
            <p class="mb-2">Best regards,</p>
            <p class="mb-2">The APIcenter team.</p>
          </div>
        </div>
        <div class="mt-2 mb-4">
          <loading-view :loading="initialLoading">
            <div class="mt-2 mb-8">
              <div>
                <form-input-header :header="trans('dashboard.applications.form.title')"/>
                <div>
                  <form-input
                    v-model="formAlias"
                    input-class="app-input-field"
                    label="Display Title"
                    required
                    type="text"
                  ></form-input>

                  <div v-for="(settings, category) in settings" :key="`s-${category}`">
                    <form-input-header v-if="showHeaders" :header="category"/>

                    <form-input
                      v-for="setting in settings"
                      :key="`se-${setting.key}`"
                      v-model="formData[setting.id].value"
                      :dependencies="setting.dependency_settings"
                      :dependency-values="formData"
                      :help-text="setting.translated_description"
                      :label="setting.translated_name"
                      :options="setting.pivot.override_options"
                      :required="setting.is_required"
                      :setting="setting"
                      :type="setting.field_type_name"
                      dependency-input-class="app-input-field"
                      input-class="app-input-field"
                    >
                    </form-input>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="errorBody" class="py-4">
              <form-input-header header="Test Connection Error"/>
              <div class="m-4 px-8">
                <div class="text-danger bg-danger-light mx-auto px-8 py-4 rounded-lg">
                  {{ errorBody }}
                </div>
              </div>
            </div>
          </loading-view>
        </div>

        <div class="flex justify-content-between" style="max-width: 50rem">
          <div class="flex">
            <button
              v-if="isFromWizard && isNewApp"
              class="button button_offset ml-auto"
              role="button"
              type="button"
              @click="goBack()"
            >
              Previous
            </button>

            <form-button
              v-if="($can('applications.update') && !isNewApp) || ($can('applications.create') && isNewApp)"
              :disabled="!isSaveButtonEnabled"
              :processing="isSaving"
              :value="trans('common.save')"
              class="button_success"
            />
          </div>
          <div class="flex">
            <form-button
              v-if="authorizeUrl && !isRemoved && !isNewApp"
              :value="trans('dashboard.applications.form.authorize')"
              button-class="button button_app"
              type="button"
              @click="handleAuthorize"
            />

            <form-button
              v-if="registerNewDevice && !isRemoved && !isNewApp"
              :value="trans('dashboard.applications.form.authorize')"
              button-class="button button_app"
              type="button"
              @click="handleQrRegistration"
            />

            <form-button
              v-if="!isRemoved && !isNewApp"
              :processing="processingTestConnection"
              :value="trans('dashboard.applications.form.test_connection')"
              button-class="button button_app"
              @click="handleTestConnection"
            />

            <button
              v-if="!isFromWizard && !isNewApp && $can('applications.delete')"
              class="tile-button has-tooltip delete"
              type="button"
              @click="handleRemoveApplication"
            >
              <svg class="icon">
                <use xlink:href="#delete-icon"></use>
              </svg>
            </button>
          </div>
        </div>
        <div v-if="qrUrl">
          <div class="flex justify-content-between" style="max-width: 50rem">
            <div class="flex">
              <p>{{ trans('dashboard.applications.form.qr_code_description') }}</p>
            </div>
          </div>
          <div class="flex justify-content-between" style="max-width: 50rem">
            <div class="flex">
              <img :src="qrUrl" alt="QRCode" :title="qrCode" style="max-width: 200px"/>
            </div>
          </div>
          <p>{{ trans('dashboard.applications.form.code') }}: {{ qrCode }}</p>
        </div>
      </form>
    </div>
    <application-delete-modal
      :app="{
        id: applicationId1,
        name: application.name,
        alias: alias,
      }"
      :name="`confirmation-${applicationId1}`"
      :user="user"
      @deleted-your-app="$inertia.visit($route('dashboard.user.applications.show', { user: user.id }))"
    />
  </loading-view>
</template>

<script>
import Swal from 'sweetalert2';
import ApplicationDeleteModal from '@/components/Dashboard/Application/DeleteModal';
import FormInput from '@/components/Dashboard/FormInput';
import FormInputHeader from '@/components/Dashboard/FormInputHeader';
import FormButton from '@/components/Shared/Form/Button';

export default {
  name: 'ApplicationForm',

  components: {
    ApplicationDeleteModal,
    FormInput,
    FormInputHeader,
    FormButton,
  },

  props: {
    user: Object,

    isNew: {
      type: Boolean,
      default: false,
    },

    applicationId: {
      type: [Number, String],
      default: null,
    },

    isFromWizard: {
      type: Boolean,
      default: false,
    },

    isSaveButtonEnabled: {
      type: Boolean,
      default: true,
    },
  },

  data: () => ({
    application: null,
    settings: {},
    formData: {},
    formAlias: '',
    initialLoading: true,
    isConnectionGood: false,
    isSaving: false,
    authorizeUrl: '',
    registerNewDevice: false,
    processingTestConnection: false,
    errorBody: null,
    qrUrl: null,
    qrCode: null,
    polling: {},
    isNewApp: false,
    alias: '',
    savedUserApplication: null,
    appId: null,
    showHeaders: false,
  }),

  async mounted() {
    await this.prepareData(this.isNew);

    this.initialLoading = false;
  },

  computed: {
    isRemoved() {
      return this.application && (this.application.status === 3 || this.application.status === 4);
    },

    applicationId1() {
      return this.appId || this.applicationId || this.$route().params.application;
    },
  },

  methods: {
    async prepareData(isNew) {
      const url = isNew
        ? `/user/${this.user.id}/application-info/new/${this.applicationId1}`
        : `/user/${this.user.id}/application-info/${this.applicationId1}`;

      await axios.get(url).then(({data}) => {
        this.application = data.data.application;
        this.settings = data.data.settings;
        this.formAlias = data.data.alias;
        this.authorizeUrl = data.data.authorizeUrl;
        this.registerNewDevice = data.data.registerNewDevice;
        this.isNewApp = data.data.isNewApp;
        this.alias = data.data.alias;
        this.showHeaders = data.data.showHeaders;
        this.qrUrl = null;
        this.qrCode = null;
        this.polling = {};

        Object.keys(this.settings).forEach(category => {
          this.settings[category].forEach(setting => {
            const value = setting.pivot.value || setting.pivot.default_value || '';

            setting.dependency_settings.forEach(dependencySetting => {
              const dependencyValue = dependencySetting.pivot.value || dependencySetting.pivot.default_value || '';

              this.formData[dependencySetting.id] = {
                setting_id: dependencySetting.id,
                value: dependencyValue,
              };
            });

            this.formData[setting.id] = {
              setting_id: setting.id,
              value: value,
            };
          });
        });
      });
    },

    async handleSaveApplication() {
      this.isSaving = true;

      if (this.isNewApp) {
        await this.addApplication();
      } else {
        await this.updateApplication();
      }

      this.isSaving = false;
    },

    async addApplication() {
      await axios
        .post(`/user/${this.user.id}/applications/${this.applicationId1}`, {
          settings: this.formData,
          alias: this.formAlias,
        })
        .then(({data}) => {
          Swal.fire({
            text: this.trans('dashboard.alerts.success_add_application'),
            icon: 'success',
            showConfirmButton: false,
            timer: 2000,
          });

          this.savedUserApplication = {
            ...data.data,
            application: this.application,
          };

          this.isNewApp = false;

          let applicationId = data.data.id;

          if (!this.isFromWizard) {
            this.$inertia.visit(
              this.$route('dashboard.user.applications.edit', {
                user: this.user.id,
                application: applicationId,
              }),
            );
          } else {
            this.appId = applicationId;
            this.prepareData(false);
          }
        })
        .catch(error => {
          Swal.fire({
            text: error.response.data.message,
            icon: 'error',
            showConfirmButton: false,
            timer: 2000,
          });
        });
    },

    async updateApplication() {
      await axios
        .patch(`/user/${this.user.id}/applications/${this.applicationId1}`, {
          settings: this.formData,
          alias: this.formAlias,
        })
        .then(() => {
          Swal.fire({
            text: this.trans('dashboard.alerts.success_update_application'),
            icon: 'success',
            showConfirmButton: false,
            timer: 2000,
          });

          this.prepareData(false);
          this.isNewApp = false;
        })
        .catch(error => {
          Swal.fire({
            text: error.response.data.message,
            icon: 'error',
            showConfirmButton: false,
            timer: 2000,
          });
        });
    },

    async handleTestConnection() {
      this.processingTestConnection = true;
      await axios
        .post(`/api/applications/${this.applicationId1}/test-connection`, {
          settings: this.formData,
        })
        .then(() => {
          Swal.fire({
            text: this.trans('dashboard.alerts.success_test_application_connection'),
            icon: 'success',
            showConfirmButton: false,
            timer: 2000,
          });
          this.isConnectionGood = true;
          this.processingTestConnection = false;
          this.errorBody = null;

          this.$emit('successful-test-connection', this.savedUserApplication);
        })
        .catch(error => {
          this.isConnectionGood = false;
          this.processingTestConnection = false;
          this.errorBody = error.response.data.body || error.response.data.message;
          Swal.fire({
            text: error.response.data.message,
            icon: 'error',
            showConfirmButton: false,
            timer: 2000,
          });
        });
    },

    handleAuthorize(e) {
      e.preventDefault();

      window.open(
        this.authorizeUrl,
        'targetWindow',
        `toolbar=no,location=no,status=no,menubar=no,scrollbars=no,resizable=yes,width=500,height=500`,
      );
    },

    async handleQrRegistration(e) {
      e.preventDefault();

      await axios
        .post(`/api/applications/${this.applicationId1}/register-new-device`, {
          settings: this.formData,
        })
        .then((data) => {
          this.processingTestConnection = false;
          this.errorBody = null;
          this.qrUrl = data.data.url;
          this.qrCode = data.data.code;
          this.polling = data.data.polling;
          this.pollQRCodeVerification(data.data.polling)
        })
        .catch(error => {
          this.isConnectionGood = false;
          this.processingTestConnection = false;
          this.errorBody = error.response.data.body || error.response.data.message;
          Swal.fire({
            text: error.response.data.message,
            icon: 'error',
            showConfirmButton: false,
            timer: 2000,
          });
        });
    },

    pollQRCodeVerification(polling) {
      let pollCounter = 0;
      let interval = setInterval(() => {
          if (++pollCounter === 60) {
            clearInterval(interval);
            this.resetPollValues();
          }
          axios.post(
            polling.url,
            polling.body,
            {headers: this.polling.headers}
          )
            .then((data) => {
              if (data.data.success === true) {
                clearInterval(interval)

                // update settings with the received information
                axios.post(`/api/applications/${this.applicationId1}/update-new-device-settings`, {
                  data: data.data.data, // This is the correct notation
                }).then(() => {
                  if (this.isFromWizard) {
                    this.prepareData(false);
                  } else {
                    window.location.reload()
                  }
                });
              }
            })
            .catch(error => {
              this.isConnectionGood = false;
              this.processingTestConnection = false;
              this.errorBody = error.response.data.body || error.response.data.message;
              Swal.fire({
                text: error.response.data.message,
                icon: 'error',
                showConfirmButton: false,
                timer: 2000,
              });
            })
        },
        1000
      )
    },

    resetPollValues() {
      this.qrUrl = null;
      this.qrCode = null;
      this.polling = null;
    },

    handleRemoveApplication() {
      this.$vfm.show(`confirmation-${this.applicationId1}`);
    },

    goBack() {
      this.$emit('go-back');
    },
  },
};
</script>

<style lang="scss">
.app-input-field {
  background: transparent;
  border: 1px solid #e2e8f0;
  border-radius: 10px;

  &:focus {
    border-color: #000000;
    background: transparent;
  }
}
</style>
